import {
  createBlockActivity,
  updateBlockActivity,
  validateBlockActivity,
} from '@/data/services/activityElement/blockServices';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { executeCode } from '@/data/services/codeExecutionService';
import Activity from '@/models/_deprecated/Activity';
import { BlockCodeActivityEditElement } from '@/models/_deprecated/ActivityElement';
import alert from '@/utils/shared/alert';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CodeElementEditForm, {
  BlockFieldsKeys,
  CodeFields,
} from './BlockCodeForm';
import { generateBlockPayload } from '@/utils/blockQuestion';
import { LanguageCodeEditorEnum } from '@/models/CodeEditorActivity';
import { ApiError } from '@/models/Errors';
import { AxiosError } from 'axios';

type onEditFn = (question: Partial<BlockCodeActivityEditElement>) => void;

export type BlockCodeServiceProps = {
  question: BlockCodeActivityEditElement;
  activity: Activity;
  onEdit: onEditFn;
  onSave: (question: BlockCodeActivityEditElement) => void;
};

export default function BlockCodeService({
  onSave,
  activity,
  onEdit,
  question,
}: BlockCodeServiceProps) {
  const [error, setError] = useState<ApiError<BlockFieldsKeys>>();
  const { t } = useTranslation('translation', {
    keyPrefix: 'activity.manageActivity.activityCode',
  });
  const { t: tBlock } = useTranslation('translation', {
    keyPrefix: 'activity.manageActivity.activityBlock',
  });
  const [validationPassed, setValidationPassed] = useState<boolean | null>(
    null,
  );

  const onCreate = async (codeQuestion: BlockCodeActivityEditElement) => {
    return await createBlockActivity(codeQuestion);
  };

  const onValidateError = () => {
    alert.error(tBlock('validateFail'));
    setError(
      new ApiError({
        response: {
          data: {
            errors: [
              {
                code: 'solutionCode',
                detail: tBlock('solution.validateFail'),
                field: 'solutionCode',
              },
            ],
          },
        },
      } as AxiosError<ApiError<BlockFieldsKeys>>),
    );
  };

  const onUpdate = async (data: Partial<CodeFields>) => {
    const oldQuestion = activity.questions.find(
      q => q.id === question.id,
    ) as BlockCodeActivityEditElement;
    const changed = buildChangedObject(oldQuestion, data);

    const initialWorkspace = generateBlockPayload(
      oldQuestion.initialWorkspace,
      data.initialWorkspace,
    );
    const solutionWorkspace = generateBlockPayload(
      oldQuestion.solutionWorkspace,
      data.solutionWorkspace,
    );

    return await updateBlockActivity(question.id, {
      ...changed,
      initialWorkspace,
      solutionWorkspace,
    });
  };

  const onSubmit = async (data: CodeFields) => {
    return question.changeStatus === 'new'
      ? await onCreate({
          ...question,
          ...data,
          activity: activity.id,
        })
      : await onUpdate(data);
  };

  const onValidateCode = async (
    baseQuestion: BlockCodeActivityEditElement,
    data: CodeFields,
  ) => {
    const questionData: BlockCodeActivityEditElement = {
      ...baseQuestion,
      ...data,
    };
    const result = await executeCode({
      solutionCode: questionData.solutionCode,
      language: LanguageCodeEditorEnum.JAVASCRIPT,
      testCases: [
        {
          input: '',
          expectedOutput: questionData.expectedOutput,
        },
      ],
    });
    return result;
  };

  const runValidateBeforeSave = async (data: CodeFields) => {
    const validationResult = await onValidateCode(question, data);
    return !!validationResult?.isValidated;
  };

  const onSaveWithValidation = async (data: CodeFields) => {
    const isValidated = await runValidateBeforeSave(data);
    if (!isValidated) {
      onValidateError();
      return;
    }
    const saved = await onSubmit(data);
    const finalValidation = await validateBlockActivity(saved.id);

    return { saved, finalValidation };
  };

  const { mutate: onValidateMutate, isPending: isValidating } = useMutation(
    ({
      baseQuestion,
      data,
    }: {
      baseQuestion: BlockCodeActivityEditElement;
      data: CodeFields;
    }) => onValidateCode(baseQuestion, data),
    {
      onError: e => {
        alert.error(getErrorMessage(e));
      },
      onSuccess: result => {
        if (!result) return;
        setValidationPassed(result.isValidated);
        if (result.isValidated) alert.success(tBlock('validateSuccess'));
        else {
          alert.error(tBlock('validateFail'));
          onValidateError();
        }
      },
    },
  );

  const handleValidate = (codeFields: CodeFields) => {
    onValidateMutate({ baseQuestion: question, data: codeFields });
  };

  const { mutate: onSaveMutate, isPending: isSaving } = useMutation(
    onSaveWithValidation,
    {
      onError: e => {
        alert.error(getErrorMessage(e));
      },
      onSuccess: data => {
        if (!data) return;
        const { saved, finalValidation } = data;
        alert.success(t('success'));
        onSave(saved);
        setValidationPassed(finalValidation.isValidated);
        if (finalValidation.isValidated)
          alert.success(tBlock('validateSuccess'));
        else alert.error(tBlock('validateFail'));
      },
    },
  );

  return (
    <CodeElementEditForm
      question={question}
      onSave={onSaveMutate}
      onValidate={handleValidate}
      onEdit={onEdit}
      isSaving={isSaving}
      isValidating={isValidating}
      validationPassed={validationPassed}
      error={error}
    />
  );
}
